import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useUserContext } from '../../../common/utils/context';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import PlanogramItem from './PlanogramItem';

const INITIAL_RENDER_COUNT = 24;
const RENDER_BATCH_SIZE = 40;

const PlanogramDetailItems = ({
	items,
	hideImages,
	hideHealthCompliance,
	batchSameProduct,
	showAdvancedView,
	showSalesLastXDays,
	showSalesLastXDaysForPicklists,
	showPickOrderForPicklists,
	itemClickPermission,
	onClickItem,
	onAddMissingQuantity,
	readOnly,
	pickListItemToggleIsPicked,
	setPickListItemIsPickedBulk,
	isConfirmStockLevels,
	isConfirmValues,
	isMarket,
	showQuantitySoldAfterRecalc,
	showExpiryDates,
	showExpiryDateButtons,
	serviceRunPlanned,
	serviceFrequencyDays,
}) => {

	const { hasPermission } = useUserContext();
	const canEditItems = hasPermission(itemClickPermission);
	const styles = useThemedStyles(createStyles);

	// Large planograms (50-200 selections) live inside a plain ScrollView, so
	// mount the item cards in frame-sized batches: the screen paints and
	// responds immediately instead of blocking on hundreds of cards at once.
	const itemCount = items?.length || 0;
	const [renderLimit, setRenderLimit] = useState(INITIAL_RENDER_COUNT);

	useEffect(() => {
		if (renderLimit >= itemCount) {
			return;
		}

		const frame = requestAnimationFrame(() => {
			setRenderLimit(previousLimit => Math.min(previousLimit + RENDER_BATCH_SIZE, itemCount));
		});

		return () => cancelAnimationFrame(frame);
	}, [itemCount, renderLimit]);

	const visibleItems = itemCount > renderLimit ? items.slice(0, renderLimit) : items;

	return (
		<View style={styles.container}>
			{visibleItems?.map(item => (
				<PlanogramItem
					onClickEdit={!readOnly && canEditItems && onClickItem ? onClickItem : undefined}
					onAddMissingQuantity={!readOnly && canEditItems && onAddMissingQuantity ? onAddMissingQuantity : undefined}
					key={`${item.id.toString()}_${batchSameProduct}`}
					item={item}
					hideImages={hideImages}
					readOnly={readOnly}
					pickListItemToggleIsPicked={pickListItemToggleIsPicked}
					setPickListItemIsPickedBulk={setPickListItemIsPickedBulk}
					isBatched={batchSameProduct}
					hideHealthCompliance={hideHealthCompliance}
					isConfirmStockLevels={isConfirmStockLevels}
					isConfirmValues={isConfirmValues}
					showAdvancedView={showAdvancedView}
					showSalesLastXDays={showSalesLastXDays}
					showSalesLastXDaysForPicklists={showSalesLastXDaysForPicklists}
					showPickOrderForPicklists={showPickOrderForPicklists}
					showQuantitySoldAfterRecalc={showQuantitySoldAfterRecalc}
					showExpiryDates={showExpiryDates}
					showExpiryDateButtons={showExpiryDateButtons}
					isMarket={isMarket}
					serviceRunPlanned={serviceRunPlanned}
					serviceFrequencyDays={serviceFrequencyDays}
				/>
			))}
		</View>
	);
};

const createStyles = ({ ui: { colors } }) => ({
	container: {
		backgroundColor: colors.canvas,
	},
});

export default React.memo(PlanogramDetailItems);
